import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeTracking.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, Plus, Pencil } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeTable from '../components/MicrositeTable/MicrositeTable'
import MicrositePlacementBadge from '../components/MicrositePlacementBadge/MicrositePlacementBadge'
import MicrositeLoadStrategyBadge from '../components/MicrositeLoadStrategyBadge/MicrositeLoadStrategyBadge'
import ToggleSwitch from '../components/ToggleSwitch/ToggleSwitch'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'
import MicrositeScriptEditorDrawer from '../components/MicrositeScriptEditorDrawer/MicrositeScriptEditorDrawer'

const MicrositeTracking = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [rows, setRows] = useState([])

    const [drawerOpen, setDrawerOpen] = useState(false)
    const [editingScript, setEditingScript] = useState(null)
    const [saving, setSaving] = useState(false)

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadScripts = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetTrackingScripts')
        if (res?.status === 'success') {
            setRows(res.response?.data ?? [])
        } else {
            showToast('Could not load tracking scripts. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadScripts()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const openNew = () => {
        setEditingScript(null)
        setDrawerOpen(true)
    }

    const openEdit = (row) => {
        setEditingScript(row)
        setDrawerOpen(true)
    }

    const handleSaveScript = async (form) => {
        setSaving(true)
        const res = editingScript
            ? await post('micrositeUpdateTrackingScript', { ID: editingScript.ID, ...form })
            : await post('micrositeCreateTrackingScript', { ...form })
        setSaving(false)

        if (res?.status === 'success') {
            showToast('Script saved successfully')
            setDrawerOpen(false)
            loadScripts()
        } else {
            showToast('Could not save script', 'error')
        }
    }

    const handleToggleActive = async (row, isActive) => {
        setRows((prev) => prev.map((r) => (r.ID === row.ID ? { ...r, isActive } : r)))
        const res = await post('micrositeUpdateTrackingScript', { ID: row.ID, ...row, isActive })
        if (res?.status !== 'success') {
            showToast('Could not update script status', 'error')
            setRows((prev) => prev.map((r) => (r.ID === row.ID ? { ...r, isActive: !isActive } : r)))
        }
    }

    const handleDelete = async (ID) => {
        const res = await post('micrositeDeleteTrackingScript', { ID })
        if (res?.status === 'success') {
            setRows((prev) => prev.filter((r) => r.ID !== ID))
            showToast('Script deleted')
        } else {
            showToast('Could not delete script', 'error')
        }
    }

    const columns = [
        {
            key: 'scriptName',
            label: 'Script',
            render: (row) => <span className={styles.name}>{row.scriptName}</span>
        },
        {
            key: 'placement',
            label: 'Placement',
            render: (row) => <MicrositePlacementBadge value={row.placement} />
        },
        {
            key: 'loadStrategy',
            label: 'Load strategy',
            render: (row) => <MicrositeLoadStrategyBadge value={row.loadStrategy} />
        },
        {
            key: 'isActive',
            label: 'Active',
            render: (row) => (
                <ToggleSwitch size="sm" checked={!!row.isActive} onChange={(v) => handleToggleActive(row, v)} />
            )
        },
        {
            key: 'actions',
            label: '',
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <button type="button" className={styles.editBtn} onClick={() => openEdit(row)}>
                        <Pencil size={13} />
                    </button>
                    <MicrositeDeleteConfirm mode="inline" onConfirm={() => handleDelete(row.ID)} />
                </div>
            )
        }
    ]

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Tracking scripts"
                subtitle="Add third-party tracking and marketing scripts to your site."
                primaryAction={{ label: 'Add script', icon: <Plus size={14} />, onClick: openNew }}
            />

            <MicrositeTable
                columns={columns}
                rows={rows}
                getRowKey={(row) => row.ID}
                loading={loading}
                emptyMessage="No tracking scripts yet"
                emptyDescription="Add one for Meta Pixel, Hotjar, Google Tag Manager, or other tools"
            />

            <MicrositeScriptEditorDrawer
                open={drawerOpen}
                initialValue={editingScript}
                onClose={() => setDrawerOpen(false)}
                onSave={handleSaveScript}
                saving={saving}
            />
        </div>
    )
}

export default MicrositeTracking